/**
 * ModoForjaTimer - Premium timer display with elegant circular progress
 */

import React from 'react';
import { Play, Pause, RotateCcw, Brain, Coffee } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useModoForja } from '@/contexts/ModoForjaContext';

export default function ModoForjaTimer() {
  const {
    timeLeft,
    isRunning,
    sessionType,
    focusDuration,
    breakDuration,
    currentCycle,
    totalCycles,
    completedSessions,
    toggleTimer,
    resetTimer,
  } = useModoForja();

  const currentDuration = sessionType === 'focus' ? focusDuration : breakDuration;
  const progress = ((currentDuration - timeLeft) / currentDuration) * 100;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex flex-col items-center">
      {/* Session Type Badge - compact and elegant */}
      <div className={cn(
        "inline-flex items-center gap-1.5 px-3 py-1 rounded-full mb-5 transition-all duration-500",
        "text-xs font-medium tracking-wide",
        sessionType === 'focus' 
          ? "bg-primary/15 text-primary border border-primary/20" 
          : "bg-success/15 text-success border border-success/20"
      )}>
        {sessionType === 'focus' ? (
          <>
            <Brain className="w-3.5 h-3.5" />
            <span>FOCO</span>
          </>
        ) : (
          <>
            <Coffee className="w-3.5 h-3.5" />
            <span>DESCANSO</span>
          </>
        )}
      </div>

      {/* Timer Circle - refined and elegant */}
      <div className="relative w-52 h-52 sm:w-56 sm:h-56 mb-5">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 200 200">
          {/* Background circle - subtle */}
          <circle
            cx="100"
            cy="100"
            r="90"
            fill="none"
            stroke="hsl(var(--secondary))"
            strokeWidth="3"
            className="opacity-30"
          />
          {/* Progress circle with glow */}
          <circle
            cx="100"
            cy="100"
            r="90"
            fill="none"
            stroke={sessionType === 'focus' ? 'hsl(var(--primary))' : 'hsl(var(--success))'}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 90}
            strokeDashoffset={2 * Math.PI * 90 * (1 - progress / 100)}
            className={cn(
              "transition-all duration-1000 ease-linear",
              isRunning && "drop-shadow-glow"
            )}
            style={{
              filter: isRunning 
                ? sessionType === 'focus' 
                  ? 'drop-shadow(0 0 12px hsl(var(--primary) / 0.6))' 
                  : 'drop-shadow(0 0 12px hsl(var(--success) / 0.6))'
                : sessionType === 'focus'
                  ? 'drop-shadow(0 0 6px hsl(var(--primary) / 0.3))'
                  : 'drop-shadow(0 0 6px hsl(var(--success) / 0.3))'
            }}
          />
        </svg>
        
        {/* Timer Text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={cn(
            "font-display text-5xl sm:text-6xl font-bold tabular-nums tracking-tight",
            sessionType === 'focus' ? "text-foreground" : "text-foreground"
          )}>
            {formatTime(timeLeft)}
          </span>
          {totalCycles !== null && (
            <span className="text-[11px] text-muted-foreground/70 mt-1 tracking-wide">
              Ciclo {currentCycle}/{totalCycles}
            </span>
          )}
        </div>
      </div>

      {/* Controls - centered and elegant */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={resetTimer}
          className="w-10 h-10 rounded-full text-muted-foreground/60 hover:text-foreground hover:bg-secondary/30"
        >
          <RotateCcw className="w-4 h-4" />
        </Button>
        
        <Button
          onClick={toggleTimer}
          className={cn(
            "w-16 h-16 rounded-full font-semibold transition-all duration-300",
            "shadow-lg hover:scale-105 active:scale-95",
            sessionType === 'focus'
              ? "bg-gradient-to-br from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground shadow-primary/30"
              : "bg-gradient-to-br from-success to-success/80 hover:from-success/90 hover:to-success/70 text-success-foreground shadow-success/30",
          )}
        >
          {isRunning ? (
            <Pause className="w-6 h-6" />
          ) : (
            <Play className="w-6 h-6 ml-0.5" />
          )}
        </Button>

        {/* Invisible spacer for symmetry */}
        <div className="w-10 h-10" />
      </div>

      {/* Completed sessions - minimal */}
      <div className="mt-5 text-center">
        <p className="text-[11px] text-muted-foreground/60 tracking-wide">
          Sessões hoje
        </p>
        <p className={cn(
          "font-display text-xl font-bold mt-0.5 tabular-nums",
          sessionType === 'focus' ? "text-primary" : "text-success"
        )}>
          {completedSessions}
        </p>
      </div>
    </div>
  );
}
